import { motion } from 'framer-motion';
import { AlertTriangle, Check, HelpCircle, Sparkles, X } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { EmptyState } from '../components/ui/EmptyState';
import { RiskBadge } from '../components/ui/RiskBadge';
import type { FeatureStatus, FoundationsResult } from '../types/org.types';

const STATUS_LABEL: Record<FeatureStatus, string> = {
  enabled: 'Enabled',
  partial: 'Partially configured',
  not_started: 'Not started',
  unknown: 'Unknown',
};

function StatusIcon({ status }: { status: FeatureStatus }) {
  if (status === 'enabled') return <Check size={14} className="text-grow" />;
  if (status === 'partial') return <AlertTriangle size={14} className="text-sunrise" />;
  if (status === 'unknown') return <HelpCircle size={14} className="text-neutral-500" />;
  return <X size={14} className="text-sunset" />;
}

function badgeToneFor(status: FeatureStatus): 'healthy' | 'medium' | 'critical' | 'neutral' {
  if (status === 'enabled') return 'healthy';
  if (status === 'partial') return 'medium';
  if (status === 'unknown') return 'neutral';
  return 'critical';
}

const STATUS_ROWS: { label: string; key: keyof FoundationsResult; positive: 'bool' | 'edition' }[] = [
  { label: 'Edition Eligible', key: 'eligible', positive: 'bool' },
  { label: 'Foundations Installed', key: 'installed', positive: 'bool' },
  { label: 'Einstein GenAI', key: 'einsteinGenAIEnabled', positive: 'bool' },
  { label: 'Agentforce', key: 'agentforceActivated', positive: 'bool' },
];

function statusTriCheck(value: boolean | 'unknown' | undefined): {
  icon: React.ReactNode;
  label: string;
  tone: 'healthy' | 'medium' | 'critical' | 'neutral';
} {
  if (value === true) return { icon: <Check size={14} />, label: 'Yes', tone: 'healthy' };
  if (value === false) return { icon: <X size={14} />, label: 'No', tone: 'critical' };
  return { icon: <HelpCircle size={14} />, label: 'Unknown', tone: 'neutral' };
}

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function FoundationsPage({ data }: { data?: FoundationsResult | null } = {}) {
  const storeFoundations = useOrgStore((s) => s.audit.foundations);
  const scanMode = data !== undefined;
  const foundations = scanMode ? data : storeFoundations;

  if (!foundations) {
    return (
      <EmptyState
        title="Foundations data not loaded"
        hint="Reconnect to scan the org again."
        icon={<Sparkles size={20} />}
      />
    );
  }

  const f = foundations;

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-grow mb-1 flex items-center gap-2">
          Free AI Audit
          <span className="text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-grow/20 text-grow border border-grow/40">
            FREE
          </span>
        </div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Salesforce Foundations</h1>
        <p className="text-sm text-neutral-500 mt-1 max-w-3xl">
          Foundations is a free add-on for Enterprise+ orgs that unlocks 14 Agentforce + Einstein AI
          features. The vast majority of clients have it installed but never switched on Einstein GenAI,
          a single toggle that gates every feature beneath it.
        </p>
      </div>

      {!f.eligible && (
        <Banner tone="critical" icon={<AlertTriangle size={16} />}>
          <strong>Org not eligible.</strong> {f.recommendation}
        </Banner>
      )}

      {f.eligible && f.installed === true && f.einsteinGenAIEnabled !== true && (
        <Banner tone="warning" icon={<AlertTriangle size={16} />}>
          <strong>Einstein Generative AI is not enabled.</strong> All free AI features below are blocked
          until this single toggle is switched on. Setup → Einstein → Einstein Generative AI → Enable.
        </Banner>
      )}

      {f.eligible && f.installed === false && (
        <Banner tone="warning" icon={<AlertTriangle size={16} />}>
          <strong>Foundations is not installed.</strong> 5-minute activation, no extra licence cost. TYRL can
          install it as part of the engagement.
        </Banner>
      )}

      {f.eligible && f.installed === true && f.einsteinGenAIEnabled === true && f.activeFeatureCount >= 7 && (
        <div data-internal-only="true">
          <Banner tone="success" icon={<Sparkles size={16} />}>
            <strong>{f.activeFeatureCount} of {f.totalFeatureCount} free features active.</strong> Strong
            Foundations adoption. Focus on advanced Agentforce + Data Cloud activation upsell.
          </Banner>
        </div>
      )}

      <motion.div
        initial="hidden"
        animate="visible"
        variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
      >
        {STATUS_ROWS.map((row) => {
          const value =
            row.key === 'eligible'
              ? f.eligible
              : row.key === 'installed'
                ? f.installed
                : row.key === 'einsteinGenAIEnabled'
                  ? f.einsteinGenAIEnabled
                  : f.agentforceActivated;
          const meta = statusTriCheck(value as boolean | 'unknown' | undefined);
          return (
            <motion.div
              key={row.key}
              variants={{
                hidden: { opacity: 0, y: 6 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
              }}
              className="bg-surface-2 border border-border rounded-md p-4 flex items-center gap-3"
            >
              <span
                className={`w-8 h-8 rounded-full flex items-center justify-center border ${
                  meta.tone === 'healthy'
                    ? 'bg-grow/15 border-grow/40 text-grow'
                    : meta.tone === 'critical'
                      ? 'bg-sunset/15 border-sunset/40 text-sunset'
                      : 'bg-surface-3 border-border text-neutral-500'
                }`}
              >
                {meta.icon}
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500">{row.label}</div>
                <div className="text-sm text-crux-light">
                  {row.key === 'eligible' && f.edition ? `${meta.label} · ${f.edition}` : meta.label}
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      <div>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Free Features</div>
            <div className="text-lg font-semibold text-crux-light mt-1">
              <span className="text-grow">{f.activeFeatureCount}</span>
              <span className="text-neutral-500"> / {f.totalFeatureCount} active</span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500">Utilisation Score</div>
            <div className={`text-2xl font-bold tabular-nums ${f.utilisationScore !== null && f.utilisationScore >= 10 ? 'text-grow' : f.utilisationScore !== null && f.utilisationScore >= 5 ? 'text-sunrise' : 'text-sunset'}`}>
              {f.utilisationLabel}
            </div>
          </div>
        </div>

        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-2">
              <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                <th className="text-left px-4 py-3 font-medium">Feature</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Free Allowance</th>
                <th className="text-left px-4 py-3 font-medium" data-internal-only="true">TYRL Service</th>
              </tr>
            </thead>
            <tbody>
              {f.features.map((feat, i) => (
                <tr key={feat.key} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <StatusIcon status={feat.status} />
                      <span className="text-crux-light">{feat.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <RiskBadge tone={badgeToneFor(feat.status)}>{STATUS_LABEL[feat.status]}</RiskBadge>
                  </td>
                  <td className="px-4 py-2.5 text-neutral-400">{feat.detail}</td>
                  <td className="px-4 py-2.5 text-neutral-400" data-internal-only="true">{feat.upsellService}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {f.eligible && (
        <div className="bg-grow/10 border border-grow/30 rounded-md p-5">
          <div className="flex items-start gap-3">
            <Sparkles size={18} className="text-grow mt-0.5" />
            <div>
              <div className="text-sm font-medium text-grow">How to activate these features</div>
              <p className="text-sm text-crux-light/80 mt-1 max-w-3xl">{f.recommendation}</p>
              <p className="text-xs text-neutral-500 mt-2" data-internal-only="true">
                Estimated activation effort: <span className="text-crux-light">~3 days with a TYRL consultant</span>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Banner({
  tone,
  icon,
  children,
}: {
  tone: 'critical' | 'warning' | 'success';
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const cls =
    tone === 'critical'
      ? 'bg-sunset/10 border-sunset/30 text-sunset'
      : tone === 'success'
        ? 'bg-grow/10 border-grow/30 text-grow'
        : 'bg-sunrise/10 border-sunrise/30 text-sunrise';
  return (
    <div className={`flex items-start gap-3 border rounded-md p-4 text-sm ${cls}`}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span className="text-crux-light/90">{children}</span>
    </div>
  );
}
